/*
 * Global style definitions for the application GUI.
 * Design tokens (colors, fonts, shadows, radius, settings) and
 * CSS class strings (Tailwind utilities + globals.css classes).
 *
 * Token source: globals.css @theme is the single source of truth.
 * Colors, shadows, and radius reference CSS variables via var().
 *
 * Migrated from TypeStyle → pure Tailwind/CSS (2026-03-17).
 * Single token source migration (2026-03-21).
 */

import { cn } from '../components/utils/cn';
import { headerSettings } from './headerSettings';

// ============ Design Tokens ============

export const colors = {
	white: 'var(--color-background)',
	black: 'var(--color-primary)',
	dark: 'var(--color-text-secondary)',
	background: 'var(--color-background)',
	foreground: 'var(--color-foreground)',
	primary: 'var(--color-primary)',
	info: 'var(--color-info)',
	error: 'var(--color-destructive)',
	success: 'var(--color-success)',
	normal: 'var(--color-text-secondary)',
	lighter: 'var(--color-muted)',
	lightest: 'var(--color-border)',
	faded: 'var(--color-border)',
	highlight: 'var(--color-primary)',
	warning: 'var(--color-warning)',
	icon: 'var(--color-icon)',
};

export const fonts = {
	normal: 'var(--font-body)',
	monospaced: 'var(--font-mono)',
};

export const fontSizes = {
	xs: 'var(--font-size-xs)',
	sm: 'var(--font-size-sm)',
	md: 'var(--font-size-md)',
	lg: 'var(--font-size-lg)',
	xl: 'var(--font-size-xl)',
	'2xl': 'var(--font-size-2xl)',
	'3xl': 'var(--font-size-3xl)',
	'4xl': 'var(--font-size-4xl)',
};

export const fontWeights = {
	normal: 'var(--font-weight-normal)',
	medium: 'var(--font-weight-medium)',
	semibold: 'var(--font-weight-semibold)',
	bold: 'var(--font-weight-bold)',
	extrabold: 'var(--font-weight-extrabold)',
};

export const lineHeights = {
	tight: 'var(--line-height-tight)',
	normal: 'var(--line-height-normal)',
	relaxed: 'var(--line-height-relaxed)',
};

export const shadows = {
	thick: 'var(--shadow-md)',
	light: 'var(--shadow-md)',
	heavy: 'var(--shadow-lg)',
};

export const radius = {
	huge: '0px',
	large: 'var(--radius-lg)',
	medium: 'var(--radius-md)',
	normal: 'var(--radius-sm)',
};

export const settings = {
	scrollHeadlineHeight: 30,
	viewContentPaddingTop: 10,
	hiddenIconWidth: 60,
	footerHeight: __PROPS__.deviceHasNotch ? 90 : __PROPS__.isAndroid ? 65 : 80,
	bigFooterHeight: (__PROPS__.deviceHasNotch ? 90 : 80) + 10,
	modalTopOffset: __PROPS__.deviceHasNotch ? 90 : 40,
};

export const animationDuration: number = 150;

// ============ Initialize CSS custom properties ============

if (typeof document !== 'undefined') {
	const root = document.documentElement;
	root.style.setProperty('--header-height', `${headerSettings.height}px`);
	root.style.setProperty('--footer-height', `${settings.footerHeight}px`);
	root.style.setProperty('--view-padding-top', `${settings.viewContentPaddingTop}px`);

	// Disable hover effects on native app
	if (__PROPS__.isNativeApp) {
		const s = document.createElement('style');
		s.textContent = 'html *:hover { pointer-events: none !important; }';
		document.head.appendChild(s);
	}
}

// ============ CSS Class Definitions (Tailwind + globals.css) ============

export namespace css {

	// --- Text styles ---

	export const NormalText = 'text-sm text-text-secondary font-light';

	export const HeaderText = 'text-xl text-primary font-extralight tracking-[0.08px]';

	export const TitleText = 'text-lg text-primary font-normal tracking-[1.08px]';

	export const HighlightText = 'text-xs text-primary font-bold';

	export const BackInDown = 'animate-back-in-down text-primary font-bold text-xs';

	// --- Shadows & shapes ---

	export const Shadow = 'shadow-lg';

	export const Rectangle = cn(Shadow, 'rounded');

	// --- Layout headings ---

	export const ScrollableViewHeadline = cn(
		HeaderText,
		'top-[-10px] bg-background w-full z-[9977]',
		'h-[30px] text-md text-primary font-normal indent-[0.4rem] p-3'
	);

	// --- App layout ---

	export const AppWrapper = 'w-full h-full overflow-hidden flex flex-col bg-background';

	export const ViewContent = (
		scrollSnap?: boolean,
		scrollHeadlines?: boolean,
		extraPadding?: number,
		fixed?: boolean
	) => ({
		container: 'view-content',
		inset: fixed ? 'view-content-inset--fixed' : 'view-content-inset',
	});

	export const AppFooter = 'app-footer';

	// --- Product design ---

	export const ProductPreview = [
		'shadow-[inset_0px_0px_15px_#fff]',
		'bg-cover bg-center bg-no-repeat',
		'mx-auto mt-[5px] mb-auto',
		'transition-all duration-[250ms] ease-in-out',
		'w-full h-[70vw]',
	].join(' ');

	export const Carousel = '!bg-background';

	export const AdjustmentCard = (selected?: boolean) => ({
		card: cn(
			NormalText,
			'relative m-0 mb-2.5 py-2.5 px-5',
			'border border-solid text-sm font-normal',
			'rounded-md bg-surface-raised cursor-pointer',
			selected
				? 'border-primary shadow-sm'
				: 'border-border shadow-sm'
		),
		header: cn(NormalText, 'uppercase text-md font-normal'),
		button: 'w-[30px] mx-2.5 p-0 rounded-full',
	});

	export const CollapsableCard = [
		'!rounded-md !mb-2.5 !p-0',
		'shadow-md',
	].join(' ');

	export const resCollapsableCard = cn(CollapsableCard, '!mr-2.5 cursor-pointer');

	export const noBoarderCollapsableCard = [
		'!snap-start !mb-0.5 !p-0',
		'!rounded-none !border-0',
		'!border-t-2 !border-t-primary/70',
	].join(' ');

	export const CollapsePanel = '';

	// --- Cart ---

	export const CartItem = () => ({
		row: cn(
			'flex relative flex-row justify-start items-center',
			'w-full overflow-hidden mt-2.5',
			'border border-border rounded-md',
			'shadow-none transition-[border-color] duration-[250ms] ease-in-out'
		),
		image: 'border border-border min-w-[55px] min-h-[55px] mr-[5px]',
	});
}
